package br.com.vvrefeicoes.portal.feature.forms

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.domain.repository.FormsRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

class FormsListController(
    private val repository: FormsRepository,
    private val scope: CoroutineScope,
    private val visibleSectors: Set<String>,
) {
    private var initialized = false
    private var job: Job? = null
    private var allItems: List<FormSummary> = emptyList()

    var items by mutableStateOf<List<FormSummary>>(emptyList())
        private set

    var loading by mutableStateOf(true)
        private set

    var busy by mutableStateOf(false)
        private set

    var query by mutableStateOf("")
        private set

    var collapsedStatuses by mutableStateOf(setOf(FormStatus.INACTIVE))
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    fun init() {
        if (initialized) return
        initialized = true
        refresh()
    }

    fun updateQuery(value: String) {
        query = value
        applyFilter()
    }

    fun refresh() {
        job?.cancel()
        job =
            scope.launch {
                loading = true
                errorMessage = null
                try {
                    allItems =
                        repository
                            .loadForms()
                            .filter { form -> form.sectors.any { it in visibleSectors } }
                    applyFilter()
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    errorMessage = throwable.message ?: "Não foi possível carregar os formulários."
                } finally {
                    loading = false
                }
            }
    }

    private fun applyFilter() {
        items =
            allItems.filter { item ->
                val term = query.trim()
                term.isEmpty() ||
                    item.title.contains(term, ignoreCase = true) ||
                    item.description.contains(term, ignoreCase = true) ||
                    item.sectors.any { it.contains(term, ignoreCase = true) } ||
                    item.status.label.contains(term, ignoreCase = true)
            }
    }

    fun toggleGroup(status: FormStatus) {
        collapsedStatuses =
            if (status in collapsedStatuses) {
                collapsedStatuses - status
            } else {
                collapsedStatuses + status
            }
    }

    fun isExpanded(status: FormStatus): Boolean = status !in collapsedStatuses

    fun copy(
        id: String,
        onCopied: (String) -> Unit,
    ) {
        if (busy) return
        scope.launch {
            busy = true
            errorMessage = null
            try {
                val copied = repository.copyForm(id)
                refresh()
                onCopied(copied.id)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível copiar o formulário."
            } finally {
                busy = false
            }
        }
    }

    fun setActive(
        id: String,
        active: Boolean,
    ) {
        if (busy) return
        scope.launch {
            busy = true
            errorMessage = null
            try {
                repository.setFormActive(id, active)
                refresh()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível alterar o status do formulário."
            } finally {
                busy = false
            }
        }
    }

    fun dismissError() {
        errorMessage = null
    }
}

class FormReadController(
    private val repository: FormsRepository,
    private val scope: CoroutineScope,
    private val formId: String,
) {
    private var initialized = false

    var form by mutableStateOf<FormDefinition?>(null)
        private set

    var loading by mutableStateOf(true)
        private set

    var busy by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    fun init() {
        if (initialized) return
        initialized = true
        load()
    }

    fun approve(onDone: () -> Unit) {
        if (busy) return
        scope.launch {
            busy = true
            errorMessage = null
            try {
                form = repository.approveForm(formId)
                onDone()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível aprovar o formulário."
            } finally {
                busy = false
            }
        }
    }

    fun reject(
        reason: String,
        onDone: () -> Unit,
    ) {
        if (busy) return
        scope.launch {
            busy = true
            errorMessage = null
            try {
                form = repository.rejectForm(formId, reason)
                onDone()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível rejeitar o formulário."
            } finally {
                busy = false
            }
        }
    }

    fun dismissError() {
        errorMessage = null
    }

    private fun load() {
        scope.launch {
            loading = true
            errorMessage = null
            try {
                form = repository.loadForm(formId)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Formulário não encontrado."
            } finally {
                loading = false
            }
        }
    }
}

class FormBuilderController(
    private val repository: FormsRepository,
    private val scope: CoroutineScope,
    private val formId: String?,
    private val readOnly: Boolean,
    private val currentUserName: String,
) {
    private var sequence = 1000

    var title by mutableStateOf("")
        private set

    var createdBy by mutableStateOf(currentUserName)
        private set

    var updatedAt by mutableStateOf("agora")
        private set

    var rejectionReason by mutableStateOf<String?>(null)
        private set

    var description by mutableStateOf("")
        private set

    var sectors by mutableStateOf<List<String>>(emptyList())
        private set

    var sections by mutableStateOf<List<FormSection>>(emptyList())
        private set

    var signatures by mutableStateOf<List<FormSignature>>(emptyList())
        private set

    var loading by mutableStateOf(formId != null)
        private set

    var saving by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var savedId by mutableStateOf(formId)
        private set

    var initialized by mutableStateOf(false)
        private set

    val isEditing: Boolean
        get() = formId != null

    fun init() {
        if (initialized) return
        initialized = true
        val id =
            formId ?: run {
                loading = false
                return
            }
        scope.launch {
            loading = true
            errorMessage = null
            try {
                val form = repository.loadForm(id)
                title = form.title
                description = form.description
                sectors = form.sectors
                sections = form.sections
                signatures = form.signatures
                createdBy = form.createdBy
                updatedAt = form.updatedAt
                rejectionReason = form.rejectionReason
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Formulário não encontrado."
            } finally {
                loading = false
            }
        }
    }

    fun updateTitle(value: String) {
        if (readOnly) return
        title = value.take(255)
    }

    fun updateDescription(value: String) {
        if (readOnly) return
        description = value.take(350)
    }

    fun updateSectors(value: Set<String>) {
        if (readOnly) return
        sectors = value.toList()
    }

    fun addSignature() {
        if (readOnly) return
        signatures =
            signatures +
            FormSignature(
                id = nextId("signature"),
                label = "",
            )
    }

    fun updateSignatureLabel(
        id: String,
        value: String,
    ) {
        if (readOnly) return
        signatures =
            signatures.map { signature ->
                if (signature.id == id) signature.copy(label = value.take(255)) else signature
            }
    }

    fun removeSignature(id: String) {
        if (readOnly) return
        signatures = signatures.filterNot { it.id == id }
    }

    fun addSection() {
        if (readOnly) return
        sections =
            sections +
            FormSection(
                id = nextId("section"),
                title = "Nova seção",
                items = emptyList(),
            )
    }

    fun updateSectionTitle(
        sectionId: String,
        value: String,
    ) {
        if (readOnly) return
        sections =
            sections.map { section ->
                if (section.id == sectionId) section.copy(title = value) else section
            }
    }

    fun removeSection(sectionId: String) {
        if (readOnly) return
        sections = sections.filterNot { it.id == sectionId }
    }

    fun moveSection(
        sectionId: String,
        direction: Int,
    ) {
        if (readOnly) return
        val index = sections.indexOfFirst { it.id == sectionId }
        val target = index + direction
        if (index < 0 || target !in sections.indices) return
        val mutable = sections.toMutableList()
        val item = mutable.removeAt(index)
        mutable.add(target, item)
        sections = mutable
    }

    fun addTextItem(sectionId: String) {
        if (readOnly) return
        updateSection(sectionId) { section ->
            section.copy(
                items =
                    section.items +
                        FormItem(
                            id = nextId("item"),
                            type = FormItemType.TEXT,
                            textHtml = "",
                        ),
            )
        }
    }

    fun updateTextItem(
        sectionId: String,
        itemId: String,
        html: String,
    ) {
        if (readOnly) return
        updateItem(sectionId, itemId) { item -> item.copy(textHtml = html) }
    }

    fun addQuestion(
        sectionId: String,
        type: FormQuestionType = FormQuestionType.TEXT,
    ) {
        if (readOnly) return
        val question =
            FormQuestion(
                id = nextId("question"),
                title = "",
                type = type,
                required = true,
                options = if (type.isChoice()) defaultOptions() else emptyList(),
            )
        updateSection(sectionId) { section ->
            section.copy(
                items =
                    section.items +
                        FormItem(
                            id = nextId("item"),
                            type = FormItemType.QUESTION,
                            question = question,
                        ),
            )
        }
    }

    private fun addChoiceTable(
        sectionId: String,
        type: FormItemType,
    ) {
        if (readOnly) return
        val weighted = type == FormItemType.CALCULATION_TABLE
        updateSection(sectionId) { section ->
            section.copy(
                items =
                    section.items +
                        FormItem(
                            id = nextId("item"),
                            type = type,
                            choiceTable =
                                FormChoiceTable(
                                    id = nextId("choice-table"),
                                    title =
                                        if (weighted) {
                                            "Nova tabela de pontuação"
                                        } else {
                                            "Nova tabela de pergunta"
                                        },
                                    columns = defaultChoiceColumns(weighted),
                                ),
                        ),
            )
        }
    }

    fun addCalculationTable(sectionId: String) {
        addChoiceTable(sectionId, FormItemType.CALCULATION_TABLE)
    }

    fun addQuestionTable(sectionId: String) {
        if (readOnly) return
        updateSection(sectionId) { section ->
            section.copy(
                items =
                    section.items +
                        FormItem(
                            id = nextId("item"),
                            type = FormItemType.QUESTION_TABLE,
                            questionTable =
                                FormQuestionTable(
                                    id = nextId("question-table"),
                                    title = "Nova tabela de perguntas",
                                ),
                        ),
            )
        }
    }

    fun updateQuestionTableTitle(
        sectionId: String,
        itemId: String,
        value: String,
    ) {
        updateQuestionTable(sectionId, itemId) { table -> table.copy(title = value.take(255)) }
    }

    fun updateQuestionTableDimensions(
        sectionId: String,
        itemId: String,
        rows: Int,
        columns: Int,
    ) {
        updateQuestionTable(sectionId, itemId) { table ->
            if (rows < 2 || columns < 1) table else table.withDimensions(rows, columns)
        }
    }

    fun updateQuestionTableHeader(
        sectionId: String,
        itemId: String,
        column: Int,
        value: String,
    ) {
        updateQuestionTable(sectionId, itemId) { table -> table.withHeader(column, value) }
    }

    fun addQuestionMatrix(sectionId: String) {
        if (readOnly) return
        updateSection(sectionId) { section ->
            section.copy(
                items =
                    section.items +
                        FormItem(
                            id = nextId("item"),
                            type = FormItemType.QUESTION_MATRIX,
                            questionMatrix =
                                FormQuestionMatrix(
                                    id = nextId("question-matrix"),
                                    title = "Nova matriz de perguntas",
                                    columns = listOf(FormQuestionMatrixColumn(nextId("question-matrix-column"), "")),
                                    rows = listOf(FormQuestionMatrixRow(nextId("question-matrix-row"), "")),
                                ),
                        ),
            )
        }
    }

    fun updateQuestionMatrixTitle(
        sectionId: String,
        itemId: String,
        value: String,
    ) {
        updateQuestionMatrix(sectionId, itemId) { matrix -> matrix.copy(title = value.take(255)) }
    }

    fun addQuestionMatrixColumn(
        sectionId: String,
        itemId: String,
    ) {
        updateQuestionMatrix(sectionId, itemId) { matrix ->
            matrix.copy(
                columns =
                    matrix.columns +
                        FormQuestionMatrixColumn(
                            id = nextId("question-matrix-column"),
                            label = "",
                        ),
            )
        }
    }

    fun updateQuestionMatrixColumnLabel(
        sectionId: String,
        itemId: String,
        columnId: String,
        value: String,
    ) {
        updateQuestionMatrix(sectionId, itemId) { matrix ->
            matrix.copy(
                columns =
                    matrix.columns.map { column ->
                        if (column.id == columnId) column.copy(label = value) else column
                    },
            )
        }
    }

    fun removeQuestionMatrixColumn(
        sectionId: String,
        itemId: String,
        columnId: String,
    ) {
        updateQuestionMatrix(sectionId, itemId) { matrix ->
            matrix.copy(columns = matrix.columns.filterNot { it.id == columnId })
        }
    }

    fun addQuestionMatrixRow(
        sectionId: String,
        itemId: String,
    ) {
        updateQuestionMatrix(sectionId, itemId) { matrix ->
            matrix.copy(
                rows =
                    matrix.rows +
                        FormQuestionMatrixRow(
                            id = nextId("question-matrix-row"),
                            label = "",
                        ),
            )
        }
    }

    fun updateQuestionMatrixRowLabel(
        sectionId: String,
        itemId: String,
        rowId: String,
        value: String,
    ) {
        updateQuestionMatrix(sectionId, itemId) { matrix ->
            matrix.copy(
                rows =
                    matrix.rows.map { row ->
                        if (row.id == rowId) row.copy(label = value) else row
                    },
            )
        }
    }

    fun removeQuestionMatrixRow(
        sectionId: String,
        itemId: String,
        rowId: String,
    ) {
        updateQuestionMatrix(sectionId, itemId) { matrix ->
            matrix.copy(rows = matrix.rows.filterNot { it.id == rowId })
        }
    }

    fun updateChoiceTableTitle(
        sectionId: String,
        itemId: String,
        value: String,
    ) {
        updateChoiceTable(sectionId, itemId) { table -> table.copy(title = value.take(255)) }
    }

    fun addChoiceColumn(
        sectionId: String,
        itemId: String,
        weighted: Boolean,
    ) {
        updateChoiceTable(sectionId, itemId) { table ->
            table.copy(
                columns =
                    table.columns +
                        FormChoiceColumn(
                            id = nextId("choice-column"),
                            label = "",
                            weight = if (weighted) 0.0 else null,
                        ),
            )
        }
    }

    fun updateChoiceColumnLabel(
        sectionId: String,
        itemId: String,
        columnId: String,
        value: String,
    ) {
        updateChoiceColumn(sectionId, itemId, columnId) { column ->
            column.copy(label = value)
        }
    }

    fun updateChoiceColumnWeight(
        sectionId: String,
        itemId: String,
        columnId: String,
        value: String,
    ) {
        updateChoiceColumn(sectionId, itemId, columnId) { column ->
            column.copy(weight = parseColumnWeight(value))
        }
    }

    fun removeChoiceColumn(
        sectionId: String,
        itemId: String,
        columnId: String,
    ) {
        updateChoiceTable(sectionId, itemId) { table ->
            table.copy(columns = table.columns.filterNot { it.id == columnId })
        }
    }

    fun addChoiceQuestion(
        sectionId: String,
        itemId: String,
        weighted: Boolean,
    ) {
        updateChoiceTable(sectionId, itemId) { table ->
            table.copy(questions = table.questions + newChoiceQuestion(weighted))
        }
    }

    fun removeChoiceQuestion(
        sectionId: String,
        itemId: String,
        questionId: String,
    ) {
        updateChoiceTable(sectionId, itemId) { table ->
            table.copy(questions = table.questions.filterNot { it.id == questionId })
        }
    }

    fun updateChoiceQuestionTitle(
        sectionId: String,
        itemId: String,
        questionId: String,
        value: String,
    ) {
        updateChoiceQuestion(sectionId, itemId, questionId) { question ->
            question.copy(title = value)
        }
    }

    fun updateChoiceQuestionWeight(
        sectionId: String,
        itemId: String,
        questionId: String,
        value: String,
    ) {
        updateChoiceQuestion(sectionId, itemId, questionId) { question ->
            question.copy(weight = parseNumericWeight(value))
        }
    }

    fun addTextTable(sectionId: String) {
        if (readOnly) return
        updateSection(sectionId) { section ->
            section.copy(
                items =
                    section.items +
                        FormItem(
                            id = nextId("item"),
                            type = FormItemType.TEXT_TABLE,
                            textTable =
                                FormTextTable(
                                    id = nextId("text-table"),
                                    title = "Nova tabela de conteúdo",
                                ),
                        ),
            )
        }
    }

    fun updateTextTableTitle(
        sectionId: String,
        itemId: String,
        value: String,
    ) {
        updateTextTable(sectionId, itemId) { table -> table.copy(title = value.take(255)) }
    }

    fun updateTextTableDimensions(
        sectionId: String,
        itemId: String,
        rows: Int,
        columns: Int,
    ) {
        updateTextTable(sectionId, itemId) { table ->
            if (rows < 1 || columns < 1) table else table.withDimensions(rows, columns)
        }
    }

    fun updateTextTableCell(
        sectionId: String,
        itemId: String,
        row: Int,
        column: Int,
        value: String,
    ) {
        updateTextTable(sectionId, itemId) { table -> table.withCell(row, column, value) }
    }

    fun updateQuestionTitle(
        sectionId: String,
        itemId: String,
        value: String,
    ) {
        updateQuestion(sectionId, itemId) { it.copy(title = value) }
    }

    fun updateQuestionType(
        sectionId: String,
        itemId: String,
        type: FormQuestionType,
    ) {
        updateQuestion(sectionId, itemId) { question ->
            question.copy(
                type = type,
                options =
                    when {
                        type.isChoice() && question.options.size < 2 -> defaultOptions()
                        type.isChoice() -> question.options
                        else -> emptyList()
                    },
            )
        }
    }

    fun toggleRequired(
        sectionId: String,
        itemId: String,
    ) {
        updateQuestion(sectionId, itemId) { it.copy(required = !it.required) }
    }

    fun toggleAcceptedFile(
        sectionId: String,
        itemId: String,
        fileType: FormFileType,
    ) {
        updateQuestion(sectionId, itemId) { question ->
            val accepted =
                if (fileType in question.acceptedFiles) {
                    question.acceptedFiles - fileType
                } else {
                    question.acceptedFiles + fileType
                }
            question.copy(acceptedFiles = accepted)
        }
    }

    fun addOption(
        sectionId: String,
        itemId: String,
    ) {
        updateQuestion(sectionId, itemId) { question ->
            question.copy(
                options =
                    question.options +
                        FormOption(
                            id = nextId("option"),
                            label = "",
                        ),
            )
        }
    }

    fun updateOptionLabel(
        sectionId: String,
        itemId: String,
        optionId: String,
        value: String,
    ) {
        updateQuestion(sectionId, itemId) { question ->
            question.copy(
                options =
                    question.options.map { option ->
                        if (option.id == optionId) option.copy(label = value) else option
                    },
            )
        }
    }

    fun removeOption(
        sectionId: String,
        itemId: String,
        optionId: String,
    ) {
        updateQuestion(sectionId, itemId) { question ->
            question.copy(options = question.options.filterNot { it.id == optionId })
        }
    }

    fun moveItem(
        sectionId: String,
        itemId: String,
        direction: Int,
    ) {
        if (readOnly) return
        updateSection(sectionId) { section ->
            val index = section.items.indexOfFirst { it.id == itemId }
            val target = index + direction
            if (index < 0 || target !in section.items.indices) {
                section
            } else {
                val items = section.items.toMutableList()
                val item = items.removeAt(index)
                items.add(target, item)
                section.copy(items = items)
            }
        }
    }

    fun removeItem(
        sectionId: String,
        itemId: String,
    ) {
        if (readOnly) return
        updateSection(sectionId) { section ->
            section.copy(items = section.items.filterNot { it.id == itemId })
        }
    }

    fun saveDraft(onSaved: (String) -> Unit) {
        if (readOnly || saving) return
        if (validate(saveOnly = true) != null) return
        scope.launch {
            saving = true
            errorMessage = null
            try {
                val saved = persistDraft()
                savedId = saved.id
                createdBy = saved.createdBy
                updatedAt = saved.updatedAt
                onSaved(saved.id)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível salvar o formulário."
            } finally {
                saving = false
            }
        }
    }

    fun submitForApproval(onSubmitted: (String) -> Unit) {
        if (readOnly || saving) return
        if (validate(saveOnly = false) != null) return
        scope.launch {
            saving = true
            errorMessage = null
            try {
                val saved = persistDraft()
                savedId = saved.id
                createdBy = saved.createdBy
                updatedAt = saved.updatedAt
                repository.submitForm(saved.id)
                onSubmitted(saved.id)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível enviar o formulário para aprovação."
            } finally {
                saving = false
            }
        }
    }

    private suspend fun persistDraft(): FormDefinition {
        val draft =
            FormDraft(
                title = title,
                description = description,
                sectors = sectors,
                sections = sections,
                signatures = signatures,
            )
        return savedId?.let { repository.updateForm(it, draft) } ?: repository.createForm(draft)
    }

    fun dismissError() {
        errorMessage = null
    }

    fun dismissRejectionReason() {
        rejectionReason = null
    }

    private fun validate(saveOnly: Boolean): String? {
        errorMessage =
            when {
                title.isBlank() -> "Informe o título do formulário."
                description.isBlank() -> "Informe a descrição do formulário."
                sectors.isEmpty() -> "Selecione pelo menos um setor responsável."
                sections.isEmpty() -> "Adicione pelo menos uma seção."
                sections.any { it.title.isBlank() } -> "Todas as seções precisam de um título."
                sections.any { it.items.isEmpty() } ->
                    "Cada seção precisa de um texto, pergunta ou tabela."
                !saveOnly && signatures.any { it.label.isBlank() } ->
                    "Preencha a label de todas as assinaturas."
                !saveOnly -> invalidQuestionMessage()
                else -> null
            }
        return errorMessage
    }

    private fun invalidQuestionMessage(): String? =
        sections
            .flatMap { section -> section.items }
            .firstNotNullOfOrNull { item ->
                val question = item.question
                val calculationTable = item.choiceTable
                val questionTable = item.questionTable
                val questionMatrix = item.questionMatrix
                val textTable = item.textTable
                when {
                    question != null && question.title.isBlank() ->
                        "Todas as perguntas precisam de um enunciado."
                    question != null &&
                        question.type.isChoice() &&
                        question.options.count { it.label.isNotBlank() } < 2 ->
                        "Perguntas de seleção precisam de pelo menos duas opções."
                    calculationTable != null && calculationTable.title.isBlank() ->
                        "Todas as tabelas de cálculo precisam de um título."
                    calculationTable != null && calculationTable.columns.count { it.label.isNotBlank() } < 2 ->
                        "A tabela de cálculo precisa de pelo menos duas colunas de resposta."
                    calculationTable != null && calculationTable.questions.isEmpty() ->
                        "Adicione pelo menos uma pergunta à tabela de cálculo."
                    calculationTable != null && calculationTable.questions.any { it.title.isBlank() } ->
                        "Todas as perguntas da tabela precisam de um enunciado."
                    questionTable != null && questionTable.title.isBlank() ->
                        "Todas as tabelas de perguntas precisam de um título."
                    questionTable != null && (questionTable.rowCount < 2 || questionTable.columnCount < 1) ->
                        "A tabela de perguntas precisa de pelo menos duas linhas e uma coluna."
                    questionTable != null && questionTable.headers.any { it.isBlank() } ->
                        "Todos os cabeçalhos das colunas precisam ser preenchidos."
                    questionMatrix != null && questionMatrix.title.isBlank() ->
                        "Todas as matrizes de perguntas precisam de um título."
                    questionMatrix != null && questionMatrix.columns.isEmpty() ->
                        "Adicione pelo menos uma coluna à matriz de perguntas."
                    questionMatrix != null && questionMatrix.rows.isEmpty() ->
                        "Adicione pelo menos uma linha à matriz de perguntas."
                    questionMatrix != null && questionMatrix.columns.any { it.label.isBlank() } ->
                        "Todos os rótulos das colunas precisam ser preenchidos."
                    questionMatrix != null && questionMatrix.rows.any { it.label.isBlank() } ->
                        "Todos os rótulos das linhas precisam ser preenchidos."
                    textTable != null && textTable.title.isBlank() ->
                        "Todas as tabelas de conteúdo precisam de um título."
                    textTable != null && textTable.cells.any { it.isBlank() } ->
                        "Todas as células da tabela de conteúdo precisam ser preenchidas."
                    else -> null
                }
            }

    private fun updateSection(
        sectionId: String,
        transform: (FormSection) -> FormSection,
    ) {
        if (readOnly) return
        sections = sections.map { section -> if (section.id == sectionId) transform(section) else section }
    }

    private fun updateItem(
        sectionId: String,
        itemId: String,
        transform: (FormItem) -> FormItem,
    ) {
        updateSection(sectionId) { section ->
            section.copy(
                items =
                    section.items.map { item ->
                        if (item.id == itemId) transform(item) else item
                    },
            )
        }
    }

    private fun updateQuestion(
        sectionId: String,
        itemId: String,
        transform: (FormQuestion) -> FormQuestion,
    ) {
        if (readOnly) return
        updateItem(sectionId, itemId) { item ->
            item.question?.let { item.copy(question = transform(it)) } ?: item
        }
    }

    private fun updateChoiceTable(
        sectionId: String,
        itemId: String,
        transform: (FormChoiceTable) -> FormChoiceTable,
    ) {
        if (readOnly) return
        updateItem(sectionId, itemId) { item ->
            item.choiceTable?.let { item.copy(choiceTable = transform(it)) } ?: item
        }
    }

    private fun updateChoiceQuestion(
        sectionId: String,
        itemId: String,
        questionId: String,
        transform: (FormChoiceQuestion) -> FormChoiceQuestion,
    ) {
        updateChoiceTable(sectionId, itemId) { table ->
            table.copy(
                questions =
                    table.questions.map { question ->
                        if (question.id == questionId) transform(question) else question
                    },
            )
        }
    }

    private fun updateChoiceColumn(
        sectionId: String,
        itemId: String,
        columnId: String,
        transform: (FormChoiceColumn) -> FormChoiceColumn,
    ) {
        updateChoiceTable(sectionId, itemId) { table ->
            table.copy(
                columns =
                    table.columns.map { column ->
                        if (column.id == columnId) transform(column) else column
                    },
            )
        }
    }

    private fun updateQuestionTable(
        sectionId: String,
        itemId: String,
        transform: (FormQuestionTable) -> FormQuestionTable,
    ) {
        if (readOnly) return
        updateItem(sectionId, itemId) { item ->
            item.questionTable?.let { item.copy(questionTable = transform(it)) } ?: item
        }
    }

    private fun updateQuestionMatrix(
        sectionId: String,
        itemId: String,
        transform: (FormQuestionMatrix) -> FormQuestionMatrix,
    ) {
        if (readOnly) return
        updateItem(sectionId, itemId) { item ->
            item.questionMatrix?.let { item.copy(questionMatrix = transform(it)) } ?: item
        }
    }

    private fun updateTextTable(
        sectionId: String,
        itemId: String,
        transform: (FormTextTable) -> FormTextTable,
    ) {
        if (readOnly) return
        updateItem(sectionId, itemId) { item ->
            item.textTable?.let { item.copy(textTable = transform(it)) } ?: item
        }
    }

    private fun newChoiceQuestion(weighted: Boolean): FormChoiceQuestion =
        FormChoiceQuestion(
            id = nextId("choice-question"),
            title = "",
            weight = if (weighted) 1 else null,
        )

    private fun defaultChoiceColumns(weighted: Boolean): List<FormChoiceColumn> =
        listOf(
            FormChoiceColumn(nextId("choice-column"), "", weight = if (weighted) 0.0 else null),
            FormChoiceColumn(nextId("choice-column"), "", weight = if (weighted) 1.0 else null),
        )

    private fun defaultOptions(): List<FormOption> =
        listOf(
            FormOption(nextId("option"), ""),
            FormOption(nextId("option"), ""),
        )

    private fun nextId(prefix: String): String = "$prefix-${sequence++}"

    private fun parseNumericWeight(value: String): Int? = value.trim().toIntOrNull()?.coerceAtLeast(0)

    private fun parseColumnWeight(value: String): Double? =
        value
            .trim()
            .replace(',', '.')
            .toDoubleOrNull()
            ?.coerceIn(0.0, 1.0)

    private fun FormQuestionType.isChoice(): Boolean = this == FormQuestionType.SINGLE_CHOICE || this == FormQuestionType.MULTIPLE_CHOICE
}
